import { Link } from "react-router-dom";
import { Star, ShoppingBag } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import ProductImage from "@/components/ProductImage";
import { toast } from "sonner";

export default function ProductCard({ product, compact = false }) {
    const { t } = useLang();
    const { add } = useCart();
    const { user } = useAuth();
    const price = product.discount_price || product.price;
    const hasDiscount = product.discount_price && product.discount_price < product.price;

    const onAdd = async (e) => {
        e.preventDefault();
        if (!user) {
            toast.error(t("loginRequired"));
            return;
        }
        try {
            await add(product.product_id, 1);
            toast.success(t("addToCart"));
        } catch (err) {
            toast.error("Error");
        }
    };

    return (
        <Link
            to={`/products/${product.product_id}`}
            className="group block bg-white rounded-2xl overflow-hidden border border-border hover:-translate-y-1 hover:shadow-lg transition-transform transition-shadow duration-300"
            data-testid={`product-card-${product.product_id}`}
        >
            <div className="aspect-square overflow-hidden bg-surface relative">
                <ProductImage
                    src={product.images?.[0]}
                    alt={product.title}
                    imageClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                {hasDiscount && (
                    <div className="absolute top-3 start-3 bg-terracotta text-white text-xs font-bold px-2.5 py-1 rounded-full">
                        -{Math.round((1 - product.discount_price / product.price) * 100)}%
                    </div>
                )}
                {product.stock === 0 && (
                    <div className="absolute inset-0 bg-black/40 grid place-items-center">
                        <span className="text-white font-bold text-sm">{t("outOfStock")}</span>
                    </div>
                )}
            </div>
            <div className="p-4">
                <h3 className={`font-bold text-espresso line-clamp-2 leading-snug ${compact ? "text-sm" : "text-base"}`}>
                    {product.title}
                </h3>
                {product.rating > 0 && (
                    <div className="flex items-center gap-1 mt-1.5 text-xs">
                        <Star className="w-3.5 h-3.5 fill-ochre text-ochre" />
                        <span className="text-muted-foreground">{product.rating.toFixed(1)}</span>
                    </div>
                )}
                <div className="mt-3 flex items-end justify-between gap-2">
                    <div>
                        <div className="text-lg font-black text-terracotta leading-none">
                            {price.toFixed(2)} <span className="text-xs font-medium text-muted-foreground">{t("SAR")}</span>
                        </div>
                        {hasDiscount && (
                            <div className="text-xs text-muted-foreground line-through mt-1">
                                {product.price.toFixed(2)} {t("SAR")}
                            </div>
                        )}
                    </div>
                    <button
                        onClick={onAdd}
                        disabled={product.stock === 0}
                        className="p-2 rounded-full bg-espresso text-white hover:bg-terracotta transition-colors disabled:opacity-40"
                        data-testid={`add-to-cart-btn-${product.product_id}`}
                        aria-label={t("addToCart")}
                    >
                        <ShoppingBag className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </Link>
    );
}
